/**
 * Product Card System - Built from Scratch
 * Handles image carousel, color swatches, hover effects, and add-to-cart modal
 */

(function() {
  'use strict';

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================

  /**
   * Update cart counter
   */
  window.updateCartCounter = function() {
    const cartIconBubble = document.getElementById('cart-icon-bubble');
    if (!cartIconBubble) return;

    const cacheBuster = '?_=' + Date.now();
    fetch('/cart.js' + cacheBuster, {
      method: 'GET',
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache'
      }
    })
      .then(response => {
        if (!response.ok) throw new Error('Failed to fetch cart: ' + response.status);
        return response.json();
      })
      .then(cartData => {
        const newCount = cartData.item_count || 0;
        updateCartCounterDisplay(cartIconBubble, newCount);
      })
      .catch(error => {
        console.error('Error fetching cart data:', error);
      });
  };

  function updateCartCounterDisplay(cartIconBubble, newCount) {
    const oldBubble = cartIconBubble.querySelector('.cart-count-bubble');

    if (newCount > 0) {
      if (oldBubble) {
        const countSpan = oldBubble.querySelector('span[aria-hidden="true"]');
        if (countSpan) {
          countSpan.textContent = newCount < 100 ? newCount.toString() : '';
        }
      } else {
        const bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';
        const span = document.createElement('span');
        span.setAttribute('aria-hidden', 'true');
        span.textContent = newCount < 100 ? newCount.toString() : '';
        bubble.appendChild(span);
        const hiddenSpan = document.createElement('span');
        hiddenSpan.className = 'visually-hidden';
        hiddenSpan.textContent = `${newCount} items in cart`;
        bubble.appendChild(hiddenSpan);
        cartIconBubble.appendChild(bubble);
      }
    } else {
      if (oldBubble) oldBubble.remove();
    }
  }

  /**
   * Add to cart function (lean: single request per click)
   * Avoids extra /cart.js + /cart/change.js calls to reduce API noise
   * and lower the chance of triggering Shopify bot/challenge pages.
   * Also manages button state: "Adding..." → "Added!" → "Add to cart".
   */
  function addToCart(variantId, card) {
    if (!variantId) {
      console.warn('Variant ID missing');
      return;
    }

    const addBtn = card ? card.querySelector('.product-card-new__add-to-cart-btn') : null;

    // Prevent double-submission from rapid clicks
    if (addBtn && addBtn.dataset.adding === 'true') {
      return;
    }

    if (addBtn) {
      addBtn.dataset.adding = 'true';
      if (!addBtn.dataset.originalText) {
        addBtn.dataset.originalText = addBtn.textContent.trim();
      }
      addBtn.disabled = true;
      addBtn.textContent = 'Adding...';
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
      }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to add to cart: ' + response.status);
        }
        return response.json();
      })
      .then((data) => {
        // Update cart counter with a single lightweight refresh
        if (typeof window.updateCartCounter === 'function') {
          setTimeout(() => {
            window.updateCartCounter();
          }, 300);
        }
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));

        if (addBtn) {
          addBtn.textContent = 'Added!';
          setTimeout(() => {
            addBtn.textContent = addBtn.dataset.originalText || 'Add to cart';
            addBtn.disabled = false;
            addBtn.dataset.adding = 'false';
          }, 1000);
        }
      })
      .catch((error) => {
        console.error('Error adding to cart:', error);
        if (addBtn) {
          addBtn.textContent = 'Error';
          setTimeout(() => {
            addBtn.textContent = addBtn.dataset.originalText || 'Add to cart';
            addBtn.disabled = false;
            addBtn.dataset.adding = 'false';
          }, 1500);
        } else {
          alert('Unable to add product to cart. Please try again.');
        }
      });
  }

  // ============================================
  // IMAGE CAROUSEL SYSTEM
  // ============================================

  function initImageCarousel(card) {
    const imageContainer = card.querySelector('.product-card-new__image-container');
    if (!imageContainer) return;

    const images = card.querySelectorAll('.product-card-new__image');
    const prevBtn = card.querySelector('.product-card-new__carousel-arrow--prev');
    const nextBtn = card.querySelector('.product-card-new__carousel-arrow--next');
    const imageCount = parseInt(card.dataset.imageCount) || images.length;
    let currentIndex = parseInt(imageContainer.dataset.currentIndex) || 0;
    let lockedImageIndex = parseInt(card.dataset.lockedImageIndex) || null;
    let hoverTimeout = null;

    if (imageCount <= 1) return;

    // Show image by index
    function showImage(index) {
      // Respect locked image if set
      if (lockedImageIndex !== null && index !== lockedImageIndex) {
        return;
      }

      images.forEach((img, i) => {
        const imgIndex = parseInt(img.dataset.imageIndex) || i;
        if (imgIndex === index) {
          img.classList.add('product-card-new__image--active');
          img.style.opacity = '1';
          img.style.zIndex = '2';
        } else {
          img.classList.remove('product-card-new__image--active');
          img.style.opacity = '0';
          img.style.zIndex = '1';
        }
      });
      currentIndex = index;
      imageContainer.dataset.currentIndex = index;
    }

    // Next image (circular)
    function nextImage() {
      if (lockedImageIndex !== null) return;
      const next = (currentIndex + 1) % imageCount;
      showImage(next);
    }

    // Previous image (circular)
    function prevImage() {
      if (lockedImageIndex !== null) return;
      const prev = (currentIndex - 1 + imageCount) % imageCount;
      showImage(prev);
    }

    // Carousel arrow handlers
    if (prevBtn) {
      prevBtn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        prevImage();
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        nextImage();
      });
    }

    // Desktop hover: transition to next image
    if (window.matchMedia('(min-width: 990px)').matches) {
      imageContainer.addEventListener('mouseenter', function() {
        if (lockedImageIndex === null && imageCount > 1) {
          clearTimeout(hoverTimeout);
          hoverTimeout = setTimeout(() => {
            if (lockedImageIndex === null) {
              nextImage();
            }
          }, 500);
        }
      });

      imageContainer.addEventListener('mouseleave', function() {
        clearTimeout(hoverTimeout);
        if (lockedImageIndex !== null) {
          showImage(lockedImageIndex);
        } else {
          showImage(0);
        }
      });
    }

    // Mobile touch swipe
    let touchStartX = 0;
    let touchEndX = 0;
    const swipeThreshold = 50;

    imageContainer.addEventListener('touchstart', function(e) {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    imageContainer.addEventListener('touchend', function(e) {
      touchEndX = e.changedTouches[0].screenX;
      const swipeDistance = touchStartX - touchEndX;

      if (Math.abs(swipeDistance) > swipeThreshold) {
        if (swipeDistance > 0) {
          // Swipe left - next
          nextImage();
        } else {
          // Swipe right - previous
          prevImage();
        }
      }
    }, { passive: true });

    // Lock/unlock image function
    card._lockImage = function(imageIndex) {
      lockedImageIndex = imageIndex;
      if (imageIndex !== null) {
        showImage(imageIndex);
      }
    };

    card._unlockImage = function() {
      lockedImageIndex = null;
    };
  }

  // ============================================
  // COLOR SWATCH SYSTEM
  // ============================================

  function initColorSwatches(card) {
    const swatches = card.querySelectorAll('.product-card-new__swatch');
    if (swatches.length === 0) return;

    let lockedColor = null;
    let lockedImageIndex = null;

    swatches.forEach(function(swatch) {
      const imageIndex = parseInt(swatch.dataset.imageIndex) || 0;
      const colorValue = swatch.dataset.colorValue;

      // Desktop hover preview
      if (window.matchMedia('(min-width: 990px)').matches) {
        swatch.addEventListener('mouseenter', function() {
          if (lockedColor === null) {
            if (card._lockImage) {
              card._lockImage(imageIndex);
            }
          }
        });
      }

      // Mobile/Desktop click/touch selection
      swatch.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();

        // Remove active from all swatches
        swatches.forEach(s => s.classList.remove('product-card-new__swatch--active'));
        swatch.classList.add('product-card-new__swatch--active');

        // Lock color and image
        lockedColor = colorValue;
        lockedImageIndex = imageIndex;

        if (card._lockImage) {
          card._lockImage(imageIndex);
        }

        // Update variant ID in add to cart button
        const variantId = swatch.dataset.variantId;
        const addToCartBtn = card.querySelector('.product-card-new__add-to-cart-btn');
        if (addToCartBtn && variantId) {
          addToCartBtn.dataset.variantId = variantId;
        }

        // Update price display if variant price is available
        if (variantId && window.PerceivedPriceSystem) {
          const productHandle = card.dataset.productHandle;
          if (productHandle) {
            fetch(`/products/${productHandle}.js`)
              .then(response => response.json())
              .then(product => {
                const variant = product.variants.find(v => v.id.toString() === variantId);
                if (variant && variant.price) {
                  const priceEl = card.querySelector('.product-card-new__price');
                  if (priceEl && window.PerceivedPriceSystem.updatePrice) {
                    window.PerceivedPriceSystem.updatePrice(priceEl, variant.price);
                  }
                }
              })
              .catch(() => {
                // Silently fail
              });
          }
        }

        // Update modal color name
        const modal = card.querySelector('.product-card-new__size-modal');
        if (modal) {
          const colorNameSpan = modal.querySelector('.product-card-new__size-modal-color-name');
          if (colorNameSpan) {
            colorNameSpan.textContent = ' (' + colorValue + ')';
          }
        }
      });
    });

    // Get locked color function
    card._getLockedColor = function() {
      return lockedColor;
    };
  }

  // ============================================
  // ADD TO CART MODAL SYSTEM
  // ============================================

  function initAddToCartModal(card) {
    const addToCartBtn = card.querySelector('.product-card-new__add-to-cart-btn');
    const sizeModal = card.querySelector('.product-card-new__size-modal');
    const hasSizes = card.dataset.hasSizes === 'true';

    if (!addToCartBtn) return;

    addToCartBtn.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();

      if (hasSizes && sizeModal) {
        // Open size selection modal
        updateModalColor(card);
        sizeModal.classList.add('product-card-new__size-modal--active');
        document.body.style.overflow = 'hidden';
      } else {
        // Direct add to cart
        const variantId = addToCartBtn.dataset.variantId;
        if (variantId) {
          addToCart(variantId, card);
        }
      }
    });

    // Size modal handlers
    if (sizeModal) {
      const sizeOptions = sizeModal.querySelectorAll('.product-card-new__size-option-btn');
      const modalAddBtn = sizeModal.querySelector('.product-card-new__size-modal-add-btn');
      const closeBtn = sizeModal.querySelector('.product-card-new__size-modal-close');
      const overlay = sizeModal.querySelector('.product-card-new__size-modal-overlay');
      let selectedVariantId = null;

      sizeOptions.forEach(function(btn) {
        btn.addEventListener('click', function() {
          if (btn.disabled) return;
          sizeOptions.forEach(b => b.classList.remove('product-card-new__size-option-btn--selected'));
          btn.classList.add('product-card-new__size-option-btn--selected');
          selectedVariantId = btn.dataset.variantId;
          if (modalAddBtn) {
            modalAddBtn.disabled = false;
          }

          // Update price display if variant price is available
          if (selectedVariantId && window.PerceivedPriceSystem) {
            const productHandle = card.dataset.productHandle;
            if (productHandle) {
              fetch(`/products/${productHandle}.js`)
                .then(response => response.json())
                .then(product => {
                  const variant = product.variants.find(v => v.id.toString() === selectedVariantId);
                  if (variant && variant.price) {
                    const priceEl = card.querySelector('.product-card-new__price');
                    if (priceEl && window.PerceivedPriceSystem.updatePrice) {
                      window.PerceivedPriceSystem.updatePrice(priceEl, variant.price);
                    }
                  }
                })
                .catch(() => {
                  // Silently fail
                });
            }
          }
        });
      });

      if (modalAddBtn) {
        modalAddBtn.addEventListener('click', function() {
          if (!selectedVariantId) return;
          addToCart(selectedVariantId, card);
          closeModal();
        });
      }

      function closeModal() {
        sizeModal.classList.remove('product-card-new__size-modal--active');
        document.body.style.overflow = '';
        sizeOptions.forEach(b => b.classList.remove('product-card-new__size-option-btn--selected'));
        if (modalAddBtn) modalAddBtn.disabled = true;
        selectedVariantId = null;
      }

      if (closeBtn) closeBtn.addEventListener('click', closeModal);
      if (overlay) overlay.addEventListener('click', closeModal);

      const modalContent = sizeModal.querySelector('.product-card-new__size-modal-content');
      if (modalContent) {
        modalContent.addEventListener('click', function(e) {
          e.stopPropagation();
        });
      }
    }
  }

  function updateModalColor(card) {
    const modal = card.querySelector('.product-card-new__size-modal');
    if (!modal) return;

    const colorNameSpan = modal.querySelector('.product-card-new__size-modal-color-name');
    if (!colorNameSpan) return;

    const lockedColor = card._getLockedColor ? card._getLockedColor() : null;
    if (lockedColor) {
      colorNameSpan.textContent = ' (' + lockedColor + ')';
    } else {
      colorNameSpan.textContent = '';
    }
  }

  // ============================================
  // WISHLIST SYSTEM (Delegated to wishlist-system.js)
  // ============================================
  // Wishlist functionality is now handled by wishlist-system.js
  // This ensures proper variant-aware storage and animations

  // ============================================
  // INITIALIZATION
  // ============================================

  function initProductCards() {
    const productCards = document.querySelectorAll('.product-card-new');

    productCards.forEach(function(card) {
      // Avoid binding duplicate listeners if this function runs multiple times (e.g. section load)
      if (card.dataset.itcInitialized === 'true') return;
      card.dataset.itcInitialized = 'true';

      initImageCarousel(card);
      initColorSwatches(card);
      initAddToCartModal(card);
    });

    // Wishlist is handled by wishlist-system.js
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductCards);
  } else {
    initProductCards();
  }

  // Re-initialize for dynamically loaded cards
  document.addEventListener('shopify:section:load', initProductCards);
  document.addEventListener('shopify:section:reorder', initProductCards);
})();
